import * as Sentry from "@sentry/react";
import { API_BASE } from "../config";

let initialized = false;

function safeNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function isFrontendSentryConfigured() {
  return !!String(process.env.REACT_APP_SENTRY_DSN || "").trim();
}

export function initFrontendMonitoring() {
  if (initialized) return;
  initialized = true;

  if (!isFrontendSentryConfigured()) return;

  const apiOrigin = (() => {
    if (!String(API_BASE || "").trim()) {
      return typeof window !== "undefined" ? window.location?.origin || null : null;
    }

    try {
      return new URL(API_BASE).origin;
    } catch {
      return API_BASE;
    }
  })();

  const tracePropagationTargets = [/^\//];
  if (apiOrigin) {
    tracePropagationTargets.unshift(apiOrigin);
  }

  Sentry.init({
    dsn: String(process.env.REACT_APP_SENTRY_DSN || "").trim(),
    environment: String(
      process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development"
    ).trim(),
    release: String(process.env.REACT_APP_SENTRY_RELEASE || "").trim() || undefined,
    tracesSampleRate: safeNumber(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0),
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets,
    sendDefaultPii: false,
  });
}

export function setMonitoringUser(user) {
  if (!isFrontendSentryConfigured()) return;

  if (user?.id) {
    Sentry.setUser({
      id: String(user.id),
      role: String(user.role || ""),
    });
    return;
  }

  Sentry.setUser(null);
}

export function captureFrontendException(error, context = {}) {
  if (!isFrontendSentryConfigured()) return;

  Sentry.withScope((scope) => {
    if (context.tags && typeof context.tags === "object") {
      scope.setTags(context.tags);
    }
    if (context.extra && typeof context.extra === "object") {
      scope.setContext("extra", context.extra);
    }
    if (context.traceId) {
      scope.setTag("trace_id", context.traceId);
    }
    Sentry.captureException(error);
  });
}

export function captureFrontendMessage(message, context = {}) {
  if (!isFrontendSentryConfigured() || !String(message || "").trim()) return;

  Sentry.withScope((scope) => {
    if (context.tags && typeof context.tags === "object") {
      scope.setTags(context.tags);
    }
    if (context.extra && typeof context.extra === "object") {
      scope.setContext("extra", context.extra);
    }
    if (context.traceId) {
      scope.setTag("trace_id", context.traceId);
    }
    Sentry.captureMessage(String(message), context.level || "error");
  });
}
